import { trimStart } from 'lodash-es';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import { useAdsTracking } from '../hooks/use-ads-tracking';
import type { AdFields } from '../types';

import ads from './ads';

interface SectionProps {
  sectionId: string;
  fallback?: React.ReactNode;
  isNewNC?: boolean;
  isLoggedOut?: boolean;
  isMockData?: boolean;
  // forces the ad to be rendered in it's small screen size
  forceMobileView?: boolean;
  // disables click handlers except for video player
  isStaticPreview?: boolean;
  onClickCallback?: () => void;
  onImpressionCallback?: () => void;
  onReportMenuOpenCallback?: () => void;
  onReportMenuCloseCallback?: () => void;
  onReportAdCallback?: () => void;
  onReportAdReasonCallback?: () => void;
}

type Ad = React.LazyExoticComponent<React.FC<AdFields>>;
export type SectionAndAdProps = SectionProps & AdFields;

export const getAdComponent = (ads: Record<string, Ad>, adType: string): Ad => {
  const accessor: string = trimStart(adType, 'Auction');

  return ads[accessor];
};

const Section: React.FC<SectionAndAdProps> = (props: SectionAndAdProps) => {
  const {
    sectionId,
    type,
    fallback = <></>,
    onClickCallback,
    isLoggedOut,
    sid,
    trackingToken,
    onImpressionCallback,
    isMockData,
  } = props;

  const Ad = getAdComponent(ads[sectionId], type);

  const handleContainerClick = (e: React.MouseEvent<HTMLElement>): void => {
    handleClick(e);
    onClickCallback?.();
  };

  const { handleClick, handleImpression } = useAdsTracking({
    isLoggedOut,
    sid,
    trackingToken,
    onImpressionCallback,
  });

  const [callbackRef] = useInView({
    onChange: handleImpression,
    threshold: 0.5,
    delay: 500,
    skip: isMockData,
  });

  return (
    <div data-qa={sectionId} className={sectionId}>
      <React.Suspense fallback={fallback}>
        <div
          ref={callbackRef}
          onKeyDown={() => {}}
          onClick={!isMockData ? handleContainerClick : undefined}
          role="button"
          tabIndex={0}
        >
          <Ad {...props} />
        </div>
      </React.Suspense>
    </div>
  );
};

export default React.memo(Section);
